import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Chip,
  Spinner,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
} from "@heroui/react";
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { useSession } from '@clerk/clerk-react';
import { API_URL } from "constants/index";
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import { sortBuildingAttributes } from 'shared/utils/sort';
import ModalAddContract from "../rentalTab/modalAddContract";
import { AttributeDocumentsSection } from "shared/components/attributeDocuments/AttributeDocumentsSection";

interface BuildingAttributeTemplate {
  id: number;
  category: string;
  ledger: string;
}

interface ContractProduct {
  id: number;
  name: string;
  contract: {
    id: number;
    phase: string;
    unit?: {
      id: string;
    };
  };
  price?: Array<{
    id: number;
    amount: number;
    effectiveDate?: string;
    recordDate?: string;
  }>;
}

interface BuildingAttribute {
  id: number;
  name: string;
  description: string;
  template: BuildingAttributeTemplate;
  contractProducts?: ContractProduct[];
  length?: number;
  width?: number;
  height?: number;
}

interface BuildingAttributesTabProps {
  building: BuildingDTO;
}

export const BuildingAttributesTab: React.FC<BuildingAttributesTabProps> = ({ building }) => {
  const { session } = useSession();
  const [attributes, setAttributes] = useState<BuildingAttribute[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Attribute-only rental modal (rent out a vacant attribute on its own, e.g. a parking).
  const [isRentModalOpen, setIsRentModalOpen] = useState(false);
  const [rentAttributeId, setRentAttributeId] = useState<number | undefined>(undefined);
  // Documents modal for a single attribute (upload/list/delete files, e.g. garage keuring).
  const [documentsAttribute, setDocumentsAttribute] = useState<BuildingAttribute | null>(null);

  useEffect(() => {
    fetchBuildingAttributes();
  }, [building.id]);

  const handleOpenRentModal = (attributeId: number) => {
    setRentAttributeId(attributeId);
    setIsRentModalOpen(true);
  };

  const handleCloseRentModal = () => {
    setIsRentModalOpen(false);
    setRentAttributeId(undefined);
  };

  const fetchBuildingAttributes = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/building-attributes/building/${building.id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        },
        session
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch attributes: ${response.statusText}`);
      }
      
      const data = await response.json();
      const attributesData = Array.isArray(data) ? data : [];
      setAttributes(sortBuildingAttributes(attributesData));
    } catch (error) {
      console.error('Error fetching building attributes:', error);
      setError('Fout bij ophalen van attributen');
      setAttributes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const renderRentedUnits = (attribute: BuildingAttribute) => {
    if (!attribute.contractProducts || attribute.contractProducts.length === 0) {
      return <span className="text-neutral-secondary-mid text-sm">Geen</span>;
    }

    const activeContracts = attribute.contractProducts.filter(cp => 
      cp.contract.phase === 'Lopende contracten' || cp.contract.phase === 'In concept'
    );

    if (activeContracts.length === 0) {
      return <span className="text-neutral-secondary-mid text-sm">Geen</span>;
    }

    const hasActiveContracts = attribute.contractProducts.some(cp => cp.contract.phase === 'Lopende contracten');
    const hasConceptContracts = attribute.contractProducts.some(cp => cp.contract.phase === 'In concept');

    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Chip 
            size="sm" 
            variant="flat" 
            className={hasActiveContracts ? "bg-moss-primary text-brick-secondary-fine" : "bg-neutral-secondary-mid text-brick-secondary-fine"}
          >
            {activeContracts.length} unit{activeContracts.length !== 1 ? 's' : ''}
          </Chip>
          {hasConceptContracts && !hasActiveContracts && (
            <Chip size="sm" variant="flat" className="bg-brick-primary text-brick-secondary-fine">
              concept
            </Chip>
          )}
        </div>
        <div className="text-xs text-neutral-secondary-mid">
          {activeContracts.slice(0, 2).map(cp => cp.contract.unit?.id || 'Onbekend').join(', ')}
          {activeContracts.length > 2 && ` +${activeContracts.length - 2} meer`}
        </div>
      </div>
    );
  };

  const renderTotalRevenue = (attribute: BuildingAttribute) => {
    if (!attribute.contractProducts || attribute.contractProducts.length === 0) {
      return <span className="text-neutral-secondary-mid text-sm">-</span>;
    }

    const activeContracts = attribute.contractProducts.filter(cp => 
      cp.contract.phase === 'Lopende contracten' || cp.contract.phase === 'In concept'
    );

    const totalRevenue = activeContracts.reduce((sum, cp) => {
      if (!cp.price || cp.price.length === 0) return sum;
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const effectivePrices = cp.price.filter(price => {
        if (!price.effectiveDate) return true;
        const effectiveDate = new Date(price.effectiveDate);
        effectiveDate.setHours(0, 0, 0, 0);
        return effectiveDate <= today;
      });
      
      if (effectivePrices.length > 0) {
        const sortedPrices = [...effectivePrices].sort((a, b) => {
          if (a.effectiveDate && b.effectiveDate) {
            return new Date(b.effectiveDate).getTime() - new Date(a.effectiveDate).getTime();
          }
          return b.id - a.id;
        });
        return sum + (sortedPrices[0]?.amount || 0);
      }
      
      return sum;
    }, 0);

    if (totalRevenue === 0) {
      return <span className="text-neutral-secondary-mid text-sm">-</span>;
    }

    const hasActiveContracts = attribute.contractProducts.some(cp => cp.contract.phase === 'Lopende contracten');
    const hasConceptContracts = attribute.contractProducts.some(cp => cp.contract.phase === 'In concept');
    
    return (
      <div className="flex items-center gap-1">
        <span className={`font-medium ${hasActiveContracts ? 'text-moss-primary' : 'text-neutral-secondary-mid'}`}>
          €{totalRevenue.toFixed(2)}
        </span>
        <span className="text-xs text-neutral-secondary-mid">/maand</span>
        {hasConceptContracts && !hasActiveContracts && (
          <span className="text-xs text-brick-primary">(concept)</span>
        )}
      </div>
    );
  };

  const getAttributeStatus = (attribute: BuildingAttribute): 'rented' | 'vacant' => {
    return attribute.contractProducts && attribute.contractProducts.length > 0 ? 'rented' : 'vacant';
  };

  const renderStatusDot = (status: 'rented' | 'vacant') => {
    return (
      <div 
        className={`w-3 h-3 rounded-full shrink-0 ${
          status === 'rented' ? 'bg-status-good' : 'bg-red-500'
        }`}
        title={status === 'rented' ? 'Gekoppeld aan contract (Verhuurd)' : 'Niet gekoppeld aan contract (Leegstaand)'}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (attributes.length === 0) {
    return (
      <Card className="bg-brick-secondary-fine">
        <CardBody className="text-center py-12">
          <div className="text-neutral-secondary-mid mb-4">
            <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-neutral-primary mb-2">Geen attributen</h3>
          <p className="text-neutral-secondary-mid">Dit gebouw heeft nog geen attributen toegewezen.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-neutral-primary">Gebouw Attributen</h2>
          <p className="text-neutral-secondary-mid">Attributen gekoppeld aan {building.id}</p>
        </div>
        <Chip color="primary" variant="flat" className="bg-brick-primary text-brick-secondary-fine">
          {attributes.length} attribu{attributes.length !== 1 ? 'ten' : 'ut'}
        </Chip>
      </div>

      <Table 
        aria-label="Building Attributes"
        className="bg-brick-secondary-fine"
        classNames={{
          wrapper: "shadow-none rounded-lg",
          th: "bg-neutral-primary text-brick-secondary-fine",
          td: "border-b border-neutral-primary/10"
        }}
      >
        <TableHeader>
          <TableColumn>NAAM</TableColumn>
          <TableColumn>TEMPLATE</TableColumn>
          <TableColumn>AFMETINGEN</TableColumn>
          <TableColumn>VERHUURDE UNITS</TableColumn>
          <TableColumn>HUURPRIJS</TableColumn>
          <TableColumn>STATUS</TableColumn>
          <TableColumn>ACTIE</TableColumn>
        </TableHeader>
        <TableBody>
          {attributes.map((attribute) => (
            <TableRow key={attribute.id} className="hover:bg-gray-50">
              <TableCell>
                <div className="flex items-start gap-2">
                  {renderStatusDot(getAttributeStatus(attribute))}
                  <div>
                    <div className="font-semibold text-neutral-primary">{attribute.name}</div>
                    {attribute.description && (
                      <div className="text-sm text-neutral-secondary-mid">{attribute.description}</div>
                    )}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <Chip size="sm" color="primary" variant="flat" className="bg-brick-primary text-brick-secondary-fine">
                    {attribute.template.category}
                  </Chip>
                  <div className="text-xs text-neutral-secondary-mid">
                    Grootboek: {attribute.template.ledger}
                  </div>
                </div>
              </TableCell>
              <TableCell>
                {(attribute.length || attribute.width || attribute.height) ? (
                  <div className="text-sm">
                    {attribute.length && <div>L: {attribute.length}m</div>}
                    {attribute.width && <div>B: {attribute.width}m</div>}
                    {attribute.height && <div>H: {attribute.height}m</div>}
                  </div>
                ) : (
                  <span className="text-neutral-secondary-mid text-sm">-</span>
                )}
              </TableCell>
              <TableCell>
                {renderRentedUnits(attribute)}
              </TableCell>
              <TableCell>
                {renderTotalRevenue(attribute)}
              </TableCell>
              <TableCell>
                {attribute.contractProducts && attribute.contractProducts.length > 0 ? (
                  <Chip size="sm" color="success" variant="flat" className="bg-status-good text-white">
                    Actief
                  </Chip>
                ) : (
                  <Chip size="sm" color="default" variant="flat" className="bg-neutral-secondary-mid text-white">
                    Beschikbaar
                  </Chip>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {getAttributeStatus(attribute) === 'vacant' && (
                    <Button
                      size="sm"
                      color="primary"
                      variant="flat"
                      className="bg-moss-primary text-brick-secondary-fine"
                      onPress={() => handleOpenRentModal(attribute.id)}
                    >
                      Verhuren
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="flat"
                    className="bg-brick-primary text-brick-secondary-fine"
                    onPress={() => setDocumentsAttribute(attribute)}
                  >
                    Documenten
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ModalAddContract
        company={building.company}
        units={(building.units as UnitDTO[]) || []}
        building={building}
        isOpen={isRentModalOpen}
        onClose={handleCloseRentModal}
        refreshContracts={fetchBuildingAttributes}
        attributeOnlyDefault={true}
        prefilledBuildingAttributeId={rentAttributeId}
      />

      <Modal
        isOpen={!!documentsAttribute}
        onClose={() => setDocumentsAttribute(null)}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            Documenten — {documentsAttribute?.name}
          </ModalHeader>
          <ModalBody className="pb-6">
            {documentsAttribute && (
              <AttributeDocumentsSection attributeId={documentsAttribute.id} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}; 